import React, { useState, useEffect } from "react"
import { Mail, Lock, Eye, EyeOff } from "lucide-react"
import Logo from "../../../../assets/logo.png"
import { useAuth } from "../../../../contexts/AuthContext"
import LoadingSpinner from "../../../../components/LoadingSpinner"
import { LegalFooter } from "../../../../components/LegalFooter"
import { useSearchParams } from 'react-router-dom';

export default function LoginForm() {
  const { signIn, signingIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const [authError, setAuthError] = useState<string | null>(null);

    // Check for authentication errors from URL params and fragments
    useEffect(() => {
      // Check query parameters
      const error = searchParams.get('auth_error');
      if (error) {
        setAuthError(decodeURIComponent(error));
        // Clean up the URL
        const url = new URL(window.location.href);
        url.searchParams.delete('auth_error');
        window.history.replaceState({}, '', url.toString());
        return;
      }
  
      // Check URL fragments for Supabase auth errors
      const hash = window.location.hash;
      if (hash) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const fragmentError = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');
        
        if (fragmentError) {
          let errorMessage = fragmentError;
          if (errorDescription) {
            errorMessage = decodeURIComponent(errorDescription.replace(/\+/g, ' '));
          }
          
          // Make error message more user-friendly
          if (fragmentError === 'access_denied' && errorDescription?.includes('expired')) {
            errorMessage = 'The email verification link has expired. Please try to go with forgot password flow.';
          }
          
          setAuthError(errorMessage);
          // Clean up the URL
          window.history.replaceState({}, '', window.location.pathname + window.location.search);
        }
      }
    }, [searchParams]);
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    const { error } = await signIn(email, password);
    if (error) {
      setError(error.message || "Failed to sign in");
    }
  };

  return (
    <>
      {signingIn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <LoadingSpinner size="lg" text="Signing in..." />
        </div>
      )}
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center gap-6 mb-8">
         <img src={Logo} alt="Logo" className="w-[160px] h-[55px]" />
        </div>

        {error && (
          <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
            {error}
          </div>
        )}
        {/* Authentication Error Display */}
        {authError && (
          <div className="w-full mb-4 px-4 md:px-0 relative z-10">
            <div className="bg-red-50 dark:bg-red-50 border border-red-200 dark:border-red-200 rounded-lg p-4 text-red-600 dark:text-red-600 text-center">
              <p className="font-medium">Authentication Error</p>
              <p className="text-sm mt-1">{authError}</p>
              <button 
                onClick={() => setAuthError(null)}
                className="mt-2 text-red-500 hover:text-red-700 dark:text-red-500 dark:hover:text-red-700 underline text-sm"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
        {/* Email */}
        <div className="space-y-1.5">
          <label htmlFor="email" className="sr-only">
            Email
          </label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0f1d2a]/50" />
            <input
              id="email"
              type="email"
              placeholder="yours@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full h-12 rounded-md border border-[#d9d9d9] bg-[#ffffff] pl-10 pr-3 py-2.5 text-base text-[#0f1d2a] placeholder:text-[#0f1d2a]/50 focus:outline-none focus:ring-2 focus:ring-[#2e4895]"
            />
          </div>
        </div>

        {/* Password */}
        <div className="space-y-1.5">
          <label htmlFor="password" className="sr-only">
            Password
          </label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0f1d2a]/50" mt-2 />
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full h-12 rounded-md border border-[#d9d9d9] bg-[#ffffff] pl-10 pr-10 py-2.5 text-base text-[#0f1d2a] placeholder:text-[#0f1d2a]/50 focus:outline-none focus:ring-2 focus:ring-[#2e4895]"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#0f1d2a]/60 hover:text-[#0f1d2a] focus:outline-none"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <a href="/forgot-password" className="mt-2 tracking-[0.5px] font-inter text-center text-sm text-[#000000] leading-[24px] font-normal mt-4 mb-4 flex justify-center">Forgot your password?{' '}</a>

        <button
          type="submit"
          disabled={signingIn}
          className="w-full rounded-md h-12 bg-[#0f1d2a] py-2.5 text-base font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Login"
        >
          {signingIn ? "SIGNING IN..." : "LOGIN"}
        </button>

        <div className="pt-10">
          <LegalFooter />
        </div>
      </form>
    </div>
    </>
  )
}
